<template>
  <div class="analysis" id="analysis">
    <div class="row">
      <div class="col-sm-12">
        <div class="m-0 my-3" id="cartCollapse">
          <div class="card">
            <div class="card-header m-0 p-0" id="cartHeading">
              <h2 class="m-0 p-0">
                <button
                  class="btn btn-link btn-block text-left text-primary"
                  type="button"
                  data-toggle="collapse"
                  data-target="#collapseCart"
                  aria-expanded="true"
                  aria-controls="collapseCart"
                >
                  <h5>
                    <i class="pull-left fa fa-calculator"></i> Analysis cart for
                    the template selection phase
                  </h5>
                </button>
              </h2>
            </div>
            <div
              id="collapseCart"
              class="collapse show"
              aria-labelledby="cartHeading"
              data-parent="#cartCollapse"
            >
              <div class="card-body">
                <div v-if="alert" role="alert" class="alert alert-info">
                  The cart initially contains five modeling templates with the
                  highest sequence similarity to the query. The templates can be
                  added and removed from the cart.
                  <button
                    type="button"
                    class="close"
                    v-on:click="hideAlert()"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>

                <h5 class="col-xs-12" v-if="selectedStructures.length === 0">
                  Select templates to perform analysis.
                </h5>

                <div
                  class="mb-2"
                  v-if="selectedStructures && selectedStructures.length > 0"
                >
                  <!-- Buttons -->
                  <div class="row mb-2">
                    <div class="col-12">
                      <button
                        type="button"
                        class="btn btn-sm btn-danger mr-1"
                        v-on:click="resetRMSD()"
                      >
                        Clear cart
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary mr-1"
                        :disabled="selectedStructures.length < 1"
                        v-on:click="modelAll()"
                      >
                        Model all
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary mr-1"
                        v-on:click="openPOSALink()"
                        :disabled="selectedStructures.length < 2"
                      >
                        Submit to POSA
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary mr-1"
                        v-bind:class="{
                          'btn-warning': dirty && selectedStructures.length > 1,
                        }"
                        v-on:click="updateRMSD()"
                        :disabled="selectedStructures.length < 2"
                      >
                        Update comparison matrix
                        <i
                          class="fas fa-sync ml-1"
                          v-if="dirty && selectedStructures.length > 1"
                        ></i>
                      </button>
                    </div>
                    <br />
                  </div>

                  <div class="row">
                    <!-- Cart content-->
                    <div class="col-sm-12 col-md-2 p-1" id="cartContentSection">
                      <div class="card">
                        <h5 class="card-header m-0 p-2">
                          Cart
                          <span class="small"
                            >({{ selectedStructures.length }} structures)</span
                          >
                        </h5>
                        <div class="card-body p-0">
                          <ul class="list-group">
                            <li
                              class="list-group-item"
                              v-for="(m, index) in selectedStructures"
                              :key="index"
                            >
                              <span
                                :style="{
                                  color: m.master.color,
                                  'font-size': '20px',
                                }"
                                aria-label="Cluster color label"
                                data-toggle="popover"
                                data-placement="top"
                                data-trigger="hover"
                                :data-content="
                                  'Member of cluster ' + m.master.label
                                "
                              >
                                <i class="fas fa-square"></i>
                              </span>
                              {{ m.name }} &nbsp;&nbsp;
                              <ModelingBtn :pdbInfo="m"></ModelingBtn>
                              <i
                                class="fas fa-times float-right"
                                v-on:click="remove(m)"
                                v-bind:title="'Remove'"
                                aria-hidden="true"
                              ></i>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div class="col-sm-12 col-md-10 mr-0 pr-0">
                      <!-- Matrix-->
                      <div class="row selfData">
                        <div
                          class="col-sm-12 col-md-5 mt-1 pl-1"
                          id="matrixSection"
                        >
                          <div class="card">
                            <h5 class="card-header m-0 p-2">
                              Comparison matrix between selected templates
                            </h5>
                            <div class="card-body p-0">
                              <div
                                class="m-3"
                                v-if="
                                  !(
                                    targetSequence.heatmapData.heatmap &&
                                    targetSequence.heatmapData.heatmap.length >
                                      0
                                  )
                                "
                              >
                                To display comparison matrix add two or more
                                structures to the cart and click
                                <button
                                  type="button"
                                  class="btn btn-sm btn-primary mr-1"
                                  disabled
                                >
                                  Update comparison matrix
                                </button>
                                above
                              </div>
                              <div v-else class="bg-light">
                                <div v-if="matrixLoading">
                                  <h5>Calculating comparison matrix...</h5>
                                  <i
                                    class="fa fa-4x fa-spinner fa-spin m-2"
                                  ></i>
                                </div>
                                <RMSDMatrix
                                  :heatmap="targetSequence.heatmapData"
                                  v-if="!matrixLoading"
                                ></RMSDMatrix>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-sm-12 col-md-7 mt-1 ml-0 pl-0">
                          <div class="card">
                            <h5 class="card-header m-0 p-2">
                              Pairwise comparison for selected pair of templates
                            </h5>
                            <ModelIntermediateStructures
                              id="modelIntermediate"
                              :pair="selectedPair"
                            ></ModelIntermediateStructures>
                            <div class="card-body p-0">
                              <PDBPairViewer
                                id="pairwiseSection"
                                :pair="selectedPair"
                              ></PDBPairViewer>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { eventBus } from "../main";
export default {
  name: "AnasylisCart",
  components: {
    PDBPairViewer: () => import("../components/PDBPairViewer"),
    ModelingBtn: () => import("../components/ModelingBtn"),
    RMSDMatrix: () => import("../components/RMSDMatrix"),
    ModelIntermediateStructures: () =>
      import("../components/ModelIntermediateStructures"),
  },
  props: {
    session: String,
    targetSequence: Object,
  },
  watch: {
    targetSequence: {
      deep: true,
      handler() {
        this.dirty = true;

        this.$nextTick(function () {
          this.reinitPopups();
          if (
            this.isFirst && this.selectedStructures.length > 1 &&
            (this.selectedStructures.length === 5 ||
              this.selectedStructures.length ===
                this.targetSequence.hits.length)
          ) {
            this.targetSequence.heatmapData.heatmap.length === 0 &&
              this.updateRMSD();
            this.isFirst = false;
          }
        });
      },
    },
  },
  computed: {
    selectedStructures: function () {
      let res = [];
      this.targetSequence.hits &&
        this.targetSequence.hits.forEach((h) => {
          h.representatives.forEach((r) => {
            r.selected && res.push(r);
          });
        });
      return res;
    },
  },
  data: function () {
    return {
      isFirst: true,
      matrixLoading: false,
      dirty: false,
      selectedPair: { pdbInfo1: {}, pdbInfo2: {} },
      alert: true,
    };
  },
  methods: {
    reinitPopups: function () {
      window.jQuery('[data-toggle="popover"]').popover({
        boundary: "window",
      });
    },
    remove: function (item) {
      item.selected = false;
      eventBus.$emit("removedFromCart", item);
    },
    resetRMSD: function () {
      this.targetSequence.heatmapData = {};
      this.matrixLoading = false; // to prevent spinner from showing
      this.dirty = false;
      this.selectedStructures.forEach((i) => (i.selected = false));
      eventBus.$emit("cartCleared", "");
    },
    modelAll: function () {
      const self = this;
      this.selectedStructures.forEach(function (i) {
        self.$dataService.modelingRequest(i);
      });
    },
    openPOSALink: function () {
      let url = "http://posa.godziklab.org/POSAn-cgi/POSA.pl?",
        q = "",
        p = "&pdbId[]=",
        c = "&chainId[]=";
      this.selectedStructures.forEach((i) => {
        q += p + i.pdb + c + i.chain;
      });
      q = q.substring(1);
      // console.log(url + q);
      window.open(url + q, "_blank");
    },
    updateRMSD: async function () {
      if (this.selectedStructures.length < 2) return;
      const self = this;
      this.matrixLoading = true;

      let pdbs = []; // ['1dfjI', '2z64A', '4fs7A', '4df0A'];
      let pmap = {};
      let meta = {};

      this.selectedStructures.forEach(function (i, idx) {
        pdbs.push(i.name);
        pmap[i.name] = { info: i, idx: idx };
        meta[i.name] = i.master;
      });

      const url =
        this.$dataService.PDBFLEX +
        "php/aliparsMatrix.php?jobID=" +
        this.session +
        "&pdbs=" +
        pdbs.join(",");

      await window.jQuery.ajax({
        type: "GET",
        async: true,
        url: url,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        success: function (response) {
          // [["1wxjA","1xcfB", 0.560,2.554,27.5,195],["1wxjA","2dzpA", 0.638,2.495,41.6,211],["1xcfB","2dzpA",0.593,3.091,32.5,207]]
          const matrix = JSON.parse(response);

          let data = {
            max: {
              // max values for all parameters
              cm_overlap: 0,
              rmsd: 0,
              seq_id: 0,
              n_res: 0,
            },
            min: {
              // max values for all parameters
              cm_overlap: 10,
              rmsd: 100,
              seq_id: 100,
              n_res: 1000,
            },
            heatmap: [], //actual data
            labels: pdbs,
            meta: meta,
            pmap: pmap,
          };

          matrix.forEach((m) => {
            // pdb1, pdb2, contact_map_overlap, rmsd, seq_id, number_of_aligned_residues
            let d = {
              pdbInfo1: pmap[m[0]].info,
              idx1: pmap[m[0]].idx,
              pdbInfo2: pmap[m[1]].info,
              idx2: pmap[m[1]].idx,
              cm_overlap: m[2],
              rmsd: m[3],
              seq_id: m[4],
              n_res: m[5],
              jobDir: null, // job dir for ddm
              ddmURL: null, // movie url
              modDDMURL: null, // models for intermediate structures url
              modDDMStatus: null,
            };
            data.max.cm_overlap = Math.max(m[2], data.max.cm_overlap);
            data.max.rmsd = Math.max(m[3], data.max.rmsd);
            data.max.seq_id = Math.max(m[4], data.max.seq_id);
            data.max.n_res = Math.max(m[5], data.max.n_res);

            data.min.cm_overlap = Math.min(m[2], data.min.cm_overlap);
            data.min.rmsd = Math.min(m[3], data.min.rmsd);
            data.min.seq_id = Math.min(m[4], data.min.seq_id);
            data.min.n_res = Math.min(m[5], data.min.n_res);

            data.heatmap.push(d);
          });

          self.targetSequence.heatmapData = data;
          self.matrixLoading = false;
        },
      });
      this.dirty = false;
    },
    selectPair: function (pair) {
      //check if pair is the same
      if (
        (this.selectedPair.pdbInfo1.name === pair.pdbInfo1.name &&
          this.selectedPair.pdbInfo2.name === pair.pdbInfo2.name) ||
        (this.selectedPair.pdbInfo2.name === pair.pdbInfo1.name &&
          this.selectedPair.pdbInfo1.name === pair.pdbInfo2.name)
      )
        return;

      this.selectedPair = pair;
    },
    hideAlert() {
      this.alert = false;
    },
  },
};
</script>

<style scoped>
</style>
